import {
  actionColumn,
  BoundNumberField,
  BoundTextField,
  column,
  Css,
  emptyCell,
  fieldCell,
  fieldColumn,
  GridDataRow,
  GridTableLayout,
  IconButton,
  numericColumn,
  PageHeaderLayout,
  simpleHeader,
  useGridTableLayoutState,
  useModal,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, useFormStates } from "@homebound/form-state";
import { useCallback, useMemo } from "react";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType, SaveGlobalPlanConstraintItemInput } from "src/generated/graphqlTypes";
import { useSaveGlobalPlanConstraintItemMutation } from "src/routes/libraries/task-catalog/components/AddConstraintItemModal.generated";
import {
  ConstraintAndAllowanceCatalogPage_GlobalPlanConstraintItemFragment,
  ConstraintAndAllowanceCatalogPageQuery,
  useConstraintAndAllowanceCatalogPageQuery,
  useDeleteGlobalPlanConstraintItemMutation,
} from "src/routes/libraries/task-catalog/ConstraintItemCatalogPage.generated";
import { catalogTasksRoute } from "src/routing/domains/libraries/task-catalog/taskCatalogPaths";
import { AddConstraintItemModal, ConstraintModalConfig } from "./components/AddConstraintItemModal";

export function ConstraintItemCatalogPage() {
  const query = useConstraintAndAllowanceCatalogPageQuery();
  const { openModal } = useModal();
  const [saveGlobalPlanConstraintItem] = useSaveGlobalPlanConstraintItemMutation();
  const [deleteGlobalPlanConstraintItem] = useDeleteGlobalPlanConstraintItemMutation();
  const isReadonly = !useFeatureFlag(FeatureFlagType.TaskCatalogAdmin);
  const layoutState = useGridTableLayoutState({
    persistedFilter: { filterDefs: {}, storageKey: "constraintItemCatalogFilter" },
    search: "client",
  });

  const { getFormState } = useFormStates<
    SaveGlobalPlanConstraintItemInput,
    ConstraintAndAllowanceCatalogPage_GlobalPlanConstraintItemFragment
  >({
    config: formConfig,
    readOnly: isReadonly,
    getId: (v) => v.id,
    autoSave: async (os) => {
      await saveGlobalPlanConstraintItem({ variables: { input: os.changedValue } });
      os.commitChanges();
    },
  });

  const onDelete = useCallback(
    async (id: string) => {
      await deleteGlobalPlanConstraintItem({
        variables: { input: { id } },
        update: (cache) => cache.evict({ id: cache.identify({ __typename: "GlobalPlanConstraintItem", id }) }),
      });
    },
    [deleteGlobalPlanConstraintItem],
  );

  const columns = useMemo(
    () => createColumns(getFormState, onDelete, isReadonly),
    [getFormState, onDelete, isReadonly],
  );
  const createTableRows = useCallback(
    (data: ConstraintAndAllowanceCatalogPageQuery | undefined) => createRows(data?.globalPlanConstraintItems ?? []),
    [],
  );

  return (
    <PageHeaderLayout
      pageHeader={{
        title: "Constraint/ Allowance Catalog",
        breadcrumbs: { breadcrumbs: [{ href: catalogTasksRoute.url(), label: "Task Catalog" }] },
        actions: !isReadonly
          ? [
              {
                label: "Create",
                onClick: () =>
                  openModal({
                    content: <AddConstraintItemModal constraintType={ConstraintModalConfig.ConstraintItem} />,
                  }),
              },
            ]
          : undefined,
      }}
    >
      <p css={Css.pxPx(24).sm.layoutContainer.$}>
        Constraints and allowances share the same items, but can be used as buffers either before or after a task.
      </p>
      <GridTableLayout
        hideEditColumns
        layoutState={layoutState}
        tableProps={{ query, createRows: createTableRows, columns, as: "virtual" }}
      />
    </PageHeaderLayout>
  );
}

type HeaderRow = { kind: "header" };
type DataRow = { kind: "data"; data: ConstraintAndAllowanceCatalogPage_GlobalPlanConstraintItemFragment };

type Row = HeaderRow | DataRow;

function createColumns(
  getFormState: (
    input: ConstraintAndAllowanceCatalogPage_GlobalPlanConstraintItemFragment,
  ) => ObjectState<SaveGlobalPlanConstraintItemInput>,
  onDelete: (id: string) => void,
  isReadonly: boolean,
) {
  return [
    fieldColumn<Row>({
      header: "Constraint/Allowance Name",
      data: (row) => ({
        content: () => {
          const os = getFormState(row);
          return <BoundTextField label="ConstraintOrAllowanceName" field={os.name} />;
        },
        value: () => row.name,
      }),
      w: "300px",
      mw: "240px",
    }),
    numericColumn<Row>({
      header: "Lag / Lead",
      data: (row) => {
        const os = getFormState(row);
        // The read-only BoundNumberField looks bad in a table cell, so render the value directly.
        if (isReadonly) return os.lagInDays.value;
        return fieldCell({ content: () => <BoundNumberField label="lagInDays" type="days" field={os.lagInDays} /> });
      },
      w: "300px",
      mw: "140px",
    }),
    column<Row>({ header: "Times Used", data: ({ numberOfTimesUsed }) => numberOfTimesUsed, mw: "120px" }),
    // only current action is delete, so only include action column if not readonly
    ...(!isReadonly
      ? [
          actionColumn<Row>({
            header: emptyCell,
            data: (row) =>
              fieldCell({
                content: () => (
                  <IconButton
                    icon="trash"
                    inc={2}
                    disabled={!row.canDelete.allowed}
                    tooltip={row.canDelete.disabledReasons.map((r) => r.message).join(", ") || undefined}
                    onClick={() => onDelete(row.id)}
                  />
                ),
              }),
            w: "50px",
          }),
        ]
      : []),
  ].compact();
}

function createRows(data: ConstraintAndAllowanceCatalogPage_GlobalPlanConstraintItemFragment[]): GridDataRow<Row>[] {
  return [simpleHeader, ...data?.map((gpci) => ({ kind: "data" as const, id: gpci.id, data: gpci }))];
}

const formConfig: ObjectConfig<SaveGlobalPlanConstraintItemInput> = {
  id: { type: "value" },
  name: { type: "value" },
  lagInDays: { type: "value" },
};
